import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFocusParam } from "@/lib/useFocus";
import { goUrl } from "@/lib/data";
import { useInsurers } from "@/lib/format";
import { PageHead } from "@/components/PageHead";
import { Glossary } from "@/components/Glossary";
import { CsrBar } from "@/components/home/InsuranceSnapshot";
import { useLead } from "@/components/layout/Layout";

export default function Insurance() {
  const { data } = useInsurers();
  useFocusParam(!!data?.items?.length);
  const openLead = useLead();
  const [sp] = useSearchParams();
  const [type, setType] = useState(sp.get("type") || "life");
  useEffect(() => { if (sp.get("type")) setType(sp.get("type")); }, [sp]);
  const items = (data?.items || []).filter((x) => x.type === type);

  return (
    <div data-testid="insurance-page">
      <PageHead eyebrow="Insurance" title="Compare insurers on what matters: paying claims." sub="Claim settlement ratio, solvency strength, network hospitals and popular plans for India's leading life and health insurers." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="inline-flex rounded-xl bg-slate-200/70 p-1 mb-6">
          {[["life", "Life & Term"], ["health", "Health"]].map(([k, l]) => <button key={k} data-testid={`insurance-tab-${k}`} onClick={() => setType(k)} className={`h-10 px-5 rounded-lg text-sm font-bold ${type === k ? "bg-white shadow" : "text-slate-500"}`}>{l}</button>)}
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <p className="font-display font-bold text-lg">Claim settlement ratio</p>
            {items.map((x) => <CsrBar key={x.id} x={x} min={type === "life" ? 95 : 80} />)}
          </div>
          <div className="lg:col-span-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table data-testid="insurers-table" className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Insurer</th><th className="px-4 py-3 text-right">CSR</th>{type === "health" && <><th className="px-4 py-3 text-right">ICR</th><th className="px-4 py-3 text-right">Hospitals</th></>}<th className="px-4 py-3 text-right">Solvency</th><th className="px-4 py-3" /></tr>
              </thead>
              <tbody>
                {items.map((x) => (
                  <tr key={x.id} data-focus={x.id} data-testid={`insurer-row-${x.id}`} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-4 min-w-[260px]"><p className="font-semibold text-slate-900">{x.name}</p><p className="text-xs text-slate-500 mt-0.5">{x.popular_plans}</p><p className="text-xs text-slate-600 mt-1">{x.best_for}</p></td>
                    <td className="px-4 py-4 text-right font-mono font-bold text-[#059669]">{x.csr.toFixed(2)}%</td>
                    {type === "health" && <><td className="px-4 py-4 text-right font-mono">{x.icr}%</td><td className="px-4 py-4 text-right font-mono">{x.network_hospitals?.toLocaleString("en-IN")}+</td></>}
                    <td className="px-4 py-4 text-right font-mono">{x.solvency}</td>
                    <td className="px-4 py-4"><button data-testid={`insurer-row-quote-${x.id}`} onClick={() => openLead({ id: x.id, company_name: x.name })} className="h-9 px-4 rounded-lg bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-bold text-xs whitespace-nowrap">Get Free Quote</button><a href={goUrl(x.id, "card")} target="_blank" rel="noopener noreferrer" data-testid={`insurer-row-site-${x.id}`} className="block mt-2 text-xs font-bold text-[#059669] hover:underline whitespace-nowrap">Visit website ↗</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-6">CSR = claims settled by number. ICR = incurred claim ratio. Solvency ≥ 1.5 is the IRDAI minimum. {data?.note}</p>
        <Glossary set={type} />
      </div>
    </div>
  );
}
